// Keyboard utilities — single source of truth for "is the user currently
// typing somewhere we shouldn't hijack their keystrokes".
//
// Why this exists (Session 19AW):
//   Each view used to roll its own check. flashcards/quiz/mock-exam only
//   tested `tagName === 'INPUT'`, missing <textarea> and contenteditable —
//   so EditableText (which uses textarea) became a hostage to global
//   shortcuts: typing "1" inside an explanation editor flipped the SRS rate
//   to "again", typing space mid-sentence flipped the flashcard. glossary
//   and mindmap had a slightly better check but still missed contenteditable
//   and IME composition.
//
// One helper, used everywhere. Adding a new keydown handler? Open the
// effect with `if (isTypingTarget(e)) return;`. There is no excuse for not
// using it — the alternative is principal's typing being silently consumed
// by some unrelated component on the same page.

/**
 * Returns true when the keyboard event originated from a "typing" target —
 * any element where the user expects characters to land in a text buffer
 * rather than be intercepted as a global shortcut.
 *
 * Covered:
 *   - <input>, <textarea>, <select>
 *   - any element with contenteditable=true (HTMLElement.isContentEditable
 *     bubbles up through inheritance, so a <span contenteditable> inside a
 *     <div> still returns true even when a parent doesn't have the attr)
 *   - any element under `[data-allow-typing]` (escape hatch for custom
 *     editors / future code-editor surfaces)
 *   - IME composition events (e.isComposing) — Chinese/Japanese/Korean
 *     input methods fire keydown DURING composition; consuming Space at
 *     that point would corrupt the user's pinyin/kana buffer
 */
export function isTypingTarget(e: KeyboardEvent): boolean {
  // IME composition: every IME consumer needs to step out of the way during
  // composition or the user's input gets eaten. Cheap to check first.
  if (e.isComposing || e.keyCode === 229) return true;

  const target = e.target as HTMLElement | null;
  if (!target) return false;

  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;

  // contenteditable=true on the target itself OR any ancestor. Browsers
  // expose this via HTMLElement.isContentEditable (which already walks
  // ancestors). jsdom's implementation is unreliable for detached / freshly-
  // built elements, so we also check the attribute directly via closest().
  if (target.isContentEditable) return true;
  if (typeof target.closest === 'function'
      && target.closest('[contenteditable]:not([contenteditable="false"])')) {
    return true;
  }

  // Explicit opt-out container (e.g. a future code editor).
  if (typeof target.closest === 'function' && target.closest('[data-allow-typing]')) {
    return true;
  }

  return false;
}
